import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Select,
  Input,
  Button,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  NumberInput,
  NumberInputField,
  Flex,
  Icon,
  useDisclosure,
  Tag,
  ButtonGroup,
  Collapse,
  Avatar,
  useToast,
  Tooltip,
  Divider
} from '@chakra-ui/react';
import {
  FiBarChart2,
  FiFilter,
  FiTrendingUp,
  FiChevronLeft,
  FiCalendar,
  FiInfo,
  FiDollarSign,
  FiChevronDown,
  FiChevronUp,
  FiSettings,
  FiRefreshCw,
  FiChevronRight,
  FiSearch,
  FiList,
  FiCheck
} from 'react-icons/fi';
import { usePerformanceData, useSessionOptions } from '../hooks/useApi';
import { formatPercent, formatCurrency, getValueColor } from '../utils/formatters';

const Performance = () => {
  // Initialize toast
  const toast = useToast();
  
  // Default date selection (today)
  const today = new Date();
  const todayString = today.toISOString().substring(0, 10);
  
  // Initial filter values
  const initialFilters = {
    start_time: `${todayString} 00:00:00`,
    end_time: `${todayString} 23:59:59`,
    session_filter: 'All',
    direction_filter: 'All',
    volume_rank_min: 1,
    volume_rank_max: 500,
    airbag_threshold: 50.0,
    margin: 10.0,
    leverage: 20,
  };
  
  // State for filters being edited
  const [filters, setFilters] = useState(initialFilters);
  
  // State for filters that have been applied (sent to API)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  
  // Track if there are unapplied changes
  const hasUnappliedChanges = JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  
  // Additional UI state
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(true);
  const { isOpen: isFiltersOpen, onToggle: onToggleFilters } = useDisclosure({ defaultIsOpen: true });
  
  // Manual loading state for filter changes
  const [isManualLoading, setIsManualLoading] = useState(false);
  
  // API calls
  const { data: sessionOptions, isLoading: isLoadingOptions } = useSessionOptions();
  const performanceQuery = usePerformanceData(appliedFilters); // Use applied filters for API
  const performanceData = performanceQuery.data?.data?.data;
  
  // Session and direction options with fallback
  const sessionOptionsMap = React.useMemo(() => {
    const fallback = {
      'All': 'All Sessions',
      'London': 'London Session (08:00-16:59 GMT)',
      'NewYork': 'New York Session (13:00-21:59 GMT)', 
      'Tokyo': 'Tokyo Session (00:00-08:59 GMT)',
      'Quiet': 'Quiet Period (22:00-23:59 GMT)'
    };
    
    // If API data is available, use it, otherwise use fallback
    if (sessionOptions?.data?.sessions) {
      return { 'All': 'All Sessions', ...sessionOptions.data.sessions };
    }
    return fallback;
  }, [sessionOptions]);
  
  const directionOptionsMap = React.useMemo(() => {
    const fallback = {
      'All': 'All Directions',
      'Long': 'Long Only',
      'Short': 'Short Only'
    };
    
    // If API data is available, use it, otherwise use fallback
    if (sessionOptions?.data?.directions) {
      return { 'All': 'All Directions', ...sessionOptions.data.directions };
    }
    return fallback;
  }, [sessionOptions]);
  
  // Show error messages from API and handle loading state
  useEffect(() => {
    if (performanceQuery.isError) {
      
      const errorMessage = performanceQuery.error?.response?.data?.message || 
                           performanceQuery.error?.message || 
                           "Please try again later";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsManualLoading(false);
    } else if (performanceQuery.data && performanceQuery.data.success === false) {
      toast({
        title: "Error",
        description: performanceQuery.data.message || "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsManualLoading(false);
    } else if (performanceQuery.data && performanceQuery.data.success !== false) {
      // Success - stop manual loading
      setIsManualLoading(false);
    }
  }, [performanceQuery.isError, performanceQuery.error, performanceQuery.data, toast]);
  
  // Colors and styling - Updated to use green tones
  const cardBg = "rgba(15, 27, 14, 0.8)";
  const borderColor = "rgba(78, 224, 53, 0.2)";
  const inputBg = "rgba(15, 27, 14, 0.7)"; // Updated to match green theme
  const buttonHoverBg = "rgba(34, 197, 94, 0.2)";
  const accentColor = "green.400";
  
  // Update the filters (without triggering API call)
  const updateFilter = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle form submission - Apply filters
  const handleApplyFilters = (e) => {
    if (e) e.preventDefault();
    setIsManualLoading(true);
    setAppliedFilters(filters);
    // Force re-fetch with new filters
    performanceQuery.refetch();
  };
  
  // Reset filters to default
  const handleResetFilters = () => {
    setFilters(initialFilters);
    // Optionally auto-apply after reset
    // setAppliedFilters(initialFilters);
  };
  
  // Format date string
  const formatDateInput = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    return dateTimeStr.replace(' ', 'T');
  };
  
  // Common button style for quick buttons
  const quickButtonStyle = {
    flex: 1,
    height: "32px",
    fontSize: "sm",
    fontWeight: "medium"
  };
  
  // Airbag helper functions
  const isAirbagOff = (value) => value === -9999;
  
  const handleAirbagQuickSelect = (value) => {
    updateFilter('airbag_threshold', value);
  };

  return (
    <Box
      className="performance-page-container"
      position="relative"
      pb={{ base: "120px", md: "80px" }}
      height="auto !important"
      minHeight="100%"
      overflowY="visible !important"
    >
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" display="flex" alignItems="center">
          <Icon as={FiBarChart2} mr={2} color={accentColor} />
          Performance
        </Heading>
        
        <Button 
          onClick={onToggleFilters} 
          size="sm" 
          leftIcon={isFiltersOpen ? <FiChevronUp /> : <FiChevronDown />}
          colorScheme="green"
          variant="outline"
          borderRadius="md"
          _hover={{
            bg: buttonHoverBg,
            transform: "translateY(-1px)",
          }}
          transition="all 0.2s"
        >
          {isFiltersOpen ? "Hide Filters" : "Show Filters"}
        </Button>
      </Flex>
      
      {/* Filters panel */}
      <Collapse in={isFiltersOpen} animateOpacity>
        <Card
          mb={6}
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
          overflow="hidden"
          borderRadius="xl"
          backdropFilter="blur(10px)"
        >
          <CardHeader
            bg="rgba(0, 0, 0, 0.3)"
            borderBottomWidth="1px"
            borderColor={borderColor}
            py={3}
          >
            <Flex justify="space-between" align="center">
              <HStack>
                <Icon as={FiFilter} color={accentColor} />
                <Heading size="md">Analysis Filters</Heading>
                {(isLoadingOptions || performanceQuery.isLoading || isManualLoading) && (
                  <Spinner size="sm" color={accentColor} />
                )}
                {hasUnappliedChanges && (
                  <Badge colorScheme="orange" variant="subtle">
                    Unsaved Changes
                  </Badge>
                )}
              </HStack>
              <HStack>
                <Button 
                  size="sm" 
                  leftIcon={<FiRefreshCw />}
                  variant="ghost"
                  onClick={handleResetFilters}
                  colorScheme="green"
                  _hover={{
                    bg: buttonHoverBg
                  }}
                >
                  Reset
                </Button>
                <Button 
                  size="sm" 
                  leftIcon={<FiCheck />}
                  onClick={handleApplyFilters}
                  colorScheme="green"
                  variant={hasUnappliedChanges ? "solid" : "outline"}
                  isDisabled={!hasUnappliedChanges}
                  _hover={{
                    bg: hasUnappliedChanges ? undefined : buttonHoverBg
                  }}
                >
                  Apply
                </Button>
              </HStack>
            </Flex>
          </CardHeader>
          
          <CardBody>
            <form onSubmit={handleApplyFilters}>
              {/* Main Filters Section */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={6}>
                {/* Date Range Section */}
                <Box>
                  <Flex align="center" mb={3}>
                    <Icon as={FiCalendar} color={accentColor} mr={2} />
                    <Heading size="sm">Date Range</Heading>
                  </Flex>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Start Date/Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formatDateInput(filters.start_time)}
                        onChange={(e) => updateFilter('start_time', e.target.value.replace('T', ' '))}
                        bg={inputBg}
                        color="white"
                        borderColor="rgba(255, 255, 255, 0.16)"
                        borderRadius="md"
                        _hover={{ borderColor: "green.300" }}
                        _focus={{ borderColor: "green.400", boxShadow: "0 0 0 1px var(--chakra-colors-green-400)" }}
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel fontSize="sm">End Date/Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formatDateInput(filters.end_time)}
                        onChange={(e) => updateFilter('end_time', e.target.value.replace('T', ' '))}
                        bg={inputBg}
                        color="white"
                        borderColor="rgba(255, 255, 255, 0.16)"
                        borderRadius="md"
                        _hover={{ borderColor: "green.300" }}
                        _focus={{ borderColor: "green.400", boxShadow: "0 0 0 1px var(--chakra-colors-green-400)" }}
                      />
                    </FormControl>
                  </SimpleGrid>
                  
                  {/* Date Navigation: Previous Day, Today, Next Day */}
                  <ButtonGroup spacing={2} mt={3} width="100%" justifyContent="space-between">
                    <Button 
                      leftIcon={<FiChevronLeft />}
                      onClick={() => {
                        const currentDate = new Date(filters.start_time.split(' ')[0]);
                        currentDate.setDate(currentDate.getDate() - 1);
                        const prevDateString = currentDate.toISOString().substring(0, 10);
                        
                        setFilters(prev => ({
                          ...prev,
                          start_time: `${prevDateString} 00:00:00`,
                          end_time: `${prevDateString} 23:59:59`
                        }));
                      }}
                      size="sm"
                      colorScheme="green"
                      variant="outline"
                      flex="1"
                      _hover={{
                        bg: buttonHoverBg
                      }}
                    >
                      Prev
                    </Button>
                    <Button 
                      onClick={() => {
                        setFilters(prev => ({
                          ...prev,
                          start_time: `${todayString} 00:00:00`,
                          end_time: `${todayString} 23:59:59`
                        }));
                      }}
                      size="sm"
                      colorScheme="green"
                      variant={
                        filters.start_time.startsWith(todayString) ? "solid" : "outline"
                      }
                      flex="1"
                    >
                      Today
                    </Button>
                    <Button 
                      rightIcon={<FiChevronRight />}
                      onClick={() => {
                        const currentDate = new Date(filters.start_time.split(' ')[0]);
                        currentDate.setDate(currentDate.getDate() + 1);
                        const nextDateString = currentDate.toISOString().substring(0, 10);
                        
                        if (currentDate <= today) {
                          setFilters(prev => ({
                            ...prev,
                            start_time: `${nextDateString} 00:00:00`,
                            end_time: `${nextDateString} 23:59:59`
                          }));
                        }
                      }}
                      size="sm"
                      colorScheme="green"
                      variant="outline"
                      flex="1"
                      isDisabled={new Date(filters.start_time.split(' ')[0]) >= today}
                      _hover={{
                        bg: buttonHoverBg
                      }}
                    >
                      Next
                    </Button>
                  </ButtonGroup>
                </Box>
                
                {/* Trading Parameters Section */}
                <Box>
                  <Flex align="center" mb={3}>
                    <Icon as={FiTrendingUp} color={accentColor} mr={2} />
                    <Heading size="sm">Trading Parameters</Heading>
                  </Flex>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Trading Session</FormLabel>
                      <Select
                        value={filters.session_filter}
                        onChange={(e) => updateFilter('session_filter', e.target.value)}
                        bg={inputBg}
                        color="white"
                        borderColor="rgba(255, 255, 255, 0.16)"
                        borderRadius="md"
                        _hover={{ borderColor: "green.300" }}
                        _focus={{ borderColor: "green.400", boxShadow: "0 0 0 1px var(--chakra-colors-green-400)" }}
                      >
                        {Object.entries(sessionOptionsMap).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel fontSize="sm">Direction</FormLabel>
                      <Select
                        value={filters.direction_filter}
                        onChange={(e) => updateFilter('direction_filter', e.target.value)}
                        bg={inputBg}
                        color="white"
                        borderColor="rgba(255, 255, 255, 0.16)"
                        borderRadius="md"
                        _hover={{ borderColor: "green.300" }}
                        _focus={{ borderColor: "green.400", boxShadow: "0 0 0 1px var(--chakra-colors-green-400)" }}
                      >
                        {Object.entries(directionOptionsMap).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </SimpleGrid>
                </Box>
              </SimpleGrid>
              
              <Divider my={4} borderColor="rgba(255, 255, 255, 0.1)" />
              
              {/* Simulation Settings */}
              <Box mb={6}>
                <Flex align="center" justify="space-between" mb={3}>
                  <HStack>
                    <Icon as={FiSettings} color={accentColor} mr={2} />
                    <Heading size="sm">Simulation Settings</Heading>
                  </HStack>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    rightIcon={showAdvancedSettings ? <FiChevronUp /> : <FiChevronDown />}
                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                    colorScheme="green"
                  >
                    {showAdvancedSettings ? 'Hide' : 'Show'}
                  </Button>
                </Flex>
                
                <Collapse in={showAdvancedSettings} animateOpacity>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4} mb={3}>
                    {/* Margin Amount Input */}
                    <Box>
                      <FormLabel fontSize="sm" mb={3}>
                        <HStack>
                          <Icon as={FiDollarSign} color={accentColor} />
                          <Text>Margin Amount (USDT)</Text>
                          <Tooltip 
                            label="Initial margin amount per trade" 
                            hasArrow 
                            placement="top"
                            closeOnClick={true}
                            closeOnMouseDown={true}
                            openDelay={200}
                            closeDelay={100}
                            shouldWrapChildren
                            portalProps={{ appendToParentPortal: false }}
                            gutter={8}
                            zIndex={9999}
                          >
                            <Box display="inline-flex">
                              <Icon as={FiInfo} color="gray.400" cursor="pointer" />
                            </Box>
                          </Tooltip>
                        </HStack>
                      </FormLabel>
                      <Box h="50px" mb={7}>
                        <Flex h="100%">
                          <Button 
                            onClick={() => updateFilter('margin', Math.max(3, filters.margin - 1))}
                            borderRightRadius="0"
                            h="100%"
                            bg="rgba(15, 27, 14, 0.7)"
                            borderColor="rgba(78, 224, 53, 0.2)"
                            borderRightWidth="0"
                            color="white"
                            _hover={{ bg: "rgba(34, 197, 94, 0.1)" }}
                            fontSize="md"
                            mr={1}
                          >
                            -
                          </Button>
                          <NumberInput
                            value={filters.margin}
                            onChange={(valueString, valueNumber) => {
                              const newValue = isNaN(valueNumber) ? 10 : valueNumber;
                              updateFilter('margin', newValue);
                            }}
                            min={5}
                            max={1000}
                            step={5}
                            borderRadius="0"
                            h="100%"
                            flex="1"
                          >
                            <NumberInputField 
                              borderRadius="0" 
                              h="100%"
                              _hover={{ borderColor: "green.300" }}
                              _focus={{ borderColor: "green.400", boxShadow: "0 0 0 1px var(--chakra-colors-green-400)" }}
                              color="white"
                              bg="rgba(15, 27, 14, 0.7)"
                              textAlign="center"
                              borderLeftWidth="0"
                              borderRightWidth="0"
                              borderColor="rgba(78, 224, 53, 0.2)"
                              fontSize="lg"
                              fontWeight="500"
                              px={0}
                            />
                          </NumberInput>
                          <Button 
                            onClick={() => updateFilter('margin', filters.margin + 1)}
                            borderLeftRadius="0"
                            h="100%"
                            bg="rgba(15, 27, 14, 0.7)"
                            borderColor="rgba(78, 224, 53, 0.2)"
                            borderLeftWidth="0"
                            color="white"
                            _hover={{ bg: "rgba(34, 197, 94, 0.1)" }}
                            fontSize="md"
                            ml={1}
                          >
                            +
                          </Button>
                        </Flex>
                      </Box>
                      
                      <ButtonGroup isAttached size="xs" width="100%" height="32px">
                        <Button 
                          onClick={() => updateFilter('margin', 10)}
                          colorScheme="green"
                          variant={filters.margin === 10 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          10
                        </Button>
                        <Button 
                          onClick={() => updateFilter('margin', 25)}
                          colorScheme="green"
                          variant={filters.margin === 25 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          25
                        </Button>
                        <Button 
                          onClick={() => updateFilter('margin', 50)}
                          colorScheme="green"
                          variant={filters.margin === 50 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          50
                        </Button>
                        <Button 
                          onClick={() => updateFilter('margin', 100)}
                          colorScheme="green"
                          variant={filters.margin === 100 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          100
                        </Button>
                      </ButtonGroup>
                    </Box>
                    
                    {/* Leverage Slider */}
                    <Box>
                      <FormLabel fontSize="sm" mb={3}>
                        <HStack>
                          <Icon as={FiTrendingUp} color={accentColor} />
                          <Text>Leverage</Text>
                          <Tooltip 
                            label="Trading leverage multiplier" 
                            hasArrow 
                            placement="top"
                            closeOnClick={true}
                            closeOnMouseDown={true}
                            openDelay={200}
                            closeDelay={100}
                            shouldWrapChildren
                            portalProps={{ appendToParentPortal: false }}
                            gutter={8}
                            zIndex={9999}
                          >
                            <Box display="inline-flex">
                              <Icon as={FiInfo} color="gray.400" cursor="pointer" />
                            </Box>
                          </Tooltip>
                        </HStack>
                      </FormLabel>
                      <Box h="40px" mb={3} display="flex" alignItems="center">
                        <Text fontSize="xl" fontWeight="500" color="white" bg="green.500" borderRadius="md" px={3} py={1}>
                          {filters.leverage}x
                        </Text>
                        <Box flex="1" px={4}>
                          <Slider
                            value={filters.leverage}
                            onChange={(val) => updateFilter('leverage', val)}
                            min={1}
                            max={50}
                            step={1}
                            focusThumbOnChange={false}
                            colorScheme="green"
                          >
                            <SliderTrack bg="rgba(255, 255, 255, 0.16)" h="3px">
                              <SliderFilledTrack />
                            </SliderTrack>
                            <SliderThumb 
                              boxSize={5} 
                              bg="white" 
                              borderColor="green.500" 
                              borderWidth="2px"
                              _focus={{ boxShadow: "0 0 0 3px rgba(72, 187, 120, 0.6)" }}
                            />
                          </Slider>
                        </Box>
                      </Box>
                      
                      <Flex justify="space-between" mb={2} px={1}>
                        <Text fontSize="xs" color="gray.400">10x</Text>
                        <Text fontSize="xs" color="gray.400">20x</Text>
                        <Text fontSize="xs" color="gray.400">30x</Text>
                        <Text fontSize="xs" color="gray.400">40x</Text>
                        <Text fontSize="xs" color="gray.400">50x</Text>
                      </Flex>
                      
                      <ButtonGroup isAttached size="xs" width="100%" height="32px">
                        <Button 
                          onClick={() => updateFilter('leverage', 5)}
                          colorScheme="green"
                          variant={filters.leverage === 5 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          5x
                        </Button>
                        <Button 
                          onClick={() => updateFilter('leverage', 10)}
                          colorScheme="green"
                          variant={filters.leverage === 10 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          10x
                        </Button>
                        <Button 
                          onClick={() => updateFilter('leverage', 20)}
                          colorScheme="green"
                          variant={filters.leverage === 20 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          20x
                        </Button>
                        <Button 
                          onClick={() => updateFilter('leverage', 50)}
                          colorScheme="green"
                          variant={filters.leverage === 50 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          50x
                        </Button>
                      </ButtonGroup>
                    </Box>
                    
                    {/* Airbag Threshold with custom controls - Updated to support negative values and Off option */}
                    <Box mb={7}>
                      <FormLabel fontSize="sm" mb={3}>
                        <HStack>
                          <Icon as={FiSettings} color={accentColor} />
                          <Text>Airbag Threshold (%)</Text>
                          <Tooltip 
                            label="Trading operations will begin once the target has been reached. (negative values allowed, Off = disabled)" 
                            hasArrow 
                            placement="top"
                            closeOnClick={true}
                            closeOnMouseDown={true}
                            openDelay={200}
                            closeDelay={100}
                            shouldWrapChildren
                            portalProps={{ appendToParentPortal: false }}
                            gutter={8}
                            zIndex={9999}
                          >
                            <Box display="inline-flex">
                              <Icon as={FiInfo} color="gray.400" cursor="pointer" />
                            </Box>
                          </Tooltip>
                        </HStack>
                      </FormLabel>
                      <Box h="50px" mb={7}>
                        <Flex h="100%">
                          <Button 
                            onClick={() => {
                              if (isAirbagOff(filters.airbag_threshold)) {
                                updateFilter('airbag_threshold', 50);
                              } else {
                                const newValue = Math.round((filters.airbag_threshold - 1) * 10) / 10;
                                updateFilter('airbag_threshold', newValue);
                              }
                            }}
                            borderRightRadius="0"
                            h="100%"
                            bg="rgba(15, 27, 14, 0.7)"
                            borderColor="rgba(78, 224, 53, 0.2)"
                            borderRightWidth="0"
                            color="white"
                            _hover={{ bg: "rgba(34, 197, 94, 0.1)" }}
                            fontSize="md"
                            mr={1}
                          >
                            -
                          </Button>
                          <NumberInput
                            value={isAirbagOff(filters.airbag_threshold) ? "" : filters.airbag_threshold}
                            onChange={(valueString, valueNumber) => {
                              if (valueString === "" || valueString === "off" || valueString.toLowerCase() === "off") {
                                updateFilter('airbag_threshold', -9999);
                              } else {
                                const newValue = isNaN(valueNumber) ? 50 : valueNumber;
                                updateFilter('airbag_threshold', newValue);
                              }
                            }}
                            precision={1}
                            step={0.5}
                            borderRadius="0"
                            h="100%"
                            flex="1"
                          >
                            <NumberInputField 
                              borderRadius="0" 
                              h="100%"
                              _hover={{ borderColor: "green.300" }}
                              _focus={{ borderColor: "green.400", boxShadow: "0 0 0 1px var(--chakra-colors-green-400)" }}
                              color="white"
                              bg="rgba(15, 27, 14, 0.7)"
                              textAlign="center"
                              borderLeftWidth="0"
                              borderRightWidth="0"
                              borderColor="rgba(78, 224, 53, 0.2)"
                              fontSize="lg"
                              fontWeight="500"
                              px={0}
                              placeholder={isAirbagOff(filters.airbag_threshold) ? "Off" : ""}
                            />
                          </NumberInput>
                          <Button 
                            onClick={() => {
                              if (isAirbagOff(filters.airbag_threshold)) {
                                updateFilter('airbag_threshold', 50);
                              } else {
                                const newValue = Math.round((filters.airbag_threshold + 1) * 10) / 10;
                                updateFilter('airbag_threshold', newValue);
                              }
                            }}
                            borderLeftRadius="0"
                            h="100%"
                            bg="rgba(15, 27, 14, 0.7)"
                            borderColor="rgba(78, 224, 53, 0.2)"
                            borderLeftWidth="0"
                            color="white"
                            _hover={{ bg: "rgba(34, 197, 94, 0.1)" }}
                            fontSize="md"
                            ml={1}
                          >
                            +
                          </Button>
                        </Flex>
                      </Box>
                      
                      <ButtonGroup isAttached size="xs" width="100%" height="32px">
                        <Button 
                          onClick={() => handleAirbagQuickSelect(-9999)}
                          colorScheme="gray"
                          variant={isAirbagOff(filters.airbag_threshold) ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          Off
                        </Button>
                        <Button 
                          onClick={() => handleAirbagQuickSelect(15)}
                          colorScheme="green"
                          variant={filters.airbag_threshold === 15 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          15%
                        </Button>
                        <Button 
                          onClick={() => handleAirbagQuickSelect(25)}
                          colorScheme="green"
                          variant={filters.airbag_threshold === 25 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          25%
                        </Button>
                        <Button 
                          onClick={() => handleAirbagQuickSelect(50)}
                          colorScheme="green"
                          variant={filters.airbag_threshold === 50 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          50%
                        </Button>
                        <Button 
                          onClick={() => handleAirbagQuickSelect(100)}
                          colorScheme="green"
                          variant={filters.airbag_threshold === 100 ? "solid" : "outline"}
                          {...quickButtonStyle}
                        >
                          100%
                        </Button>
                      </ButtonGroup>
                    </Box>
                  </SimpleGrid>
                </Collapse>
              </Box>
              
              {/* Apply Button for Mobile/Bottom of Form */}
              <Box mt={4} display={{ base: "block", md: "none" }}>
                <Button 
                  size="md" 
                  leftIcon={<FiCheck />}
                  onClick={handleApplyFilters}
                  colorScheme="green"
                  variant={hasUnappliedChanges ? "solid" : "outline"}
                  isDisabled={!hasUnappliedChanges}
                  width="100%"
                  _hover={{
                    bg: hasUnappliedChanges ? undefined : buttonHoverBg
                  }}
                >
                  Apply Filters
                </Button>
              </Box>
            </form>
          </CardBody>
        </Card>
      </Collapse>
      
      {/* Active Filters Display - Show applied filters */}
      {(appliedFilters.session_filter !== 'All' || appliedFilters.direction_filter !== 'All' || 
        appliedFilters.leverage !== 20 || appliedFilters.margin !== 10 || appliedFilters.airbag_threshold !== 50) && (
        <Box mb={6} display="flex" flexWrap="wrap" alignItems="center">
          <Text color="gray.400" mr={3} fontSize="sm">Active Filters:</Text>
          
          {appliedFilters.session_filter !== 'All' && (
            <Tag
              mr={2}
              mb={2}
              colorScheme="green"
              variant="subtle"
              borderRadius="full"
              size="sm"
            >
              <Box as="span" mr={1}>🕒</Box>
              {sessionOptionsMap[appliedFilters.session_filter] || appliedFilters.session_filter}
            </Tag>
          )}
          
          {appliedFilters.direction_filter !== 'All' && (
            <Tag
              mr={2}
              mb={2}
              colorScheme={appliedFilters.direction_filter === 'Long' ? 'green' : 'red'}
              variant="subtle"
              borderRadius="full"
              size="sm"
            >
              <Box as="span" mr={1}>{appliedFilters.direction_filter === 'Long' ? '↗️' : '↘️'}</Box>
              {directionOptionsMap[appliedFilters.direction_filter] || appliedFilters.direction_filter}
            </Tag>
          )}
          
          {appliedFilters.leverage !== 20 && (
            <Tag
              mr={2}
              mb={2}
              colorScheme="green"
              variant="subtle"
              borderRadius="full"
              size="sm"
            >
              <Box as="span" mr={1}>⚡</Box>
              {appliedFilters.leverage}x Leverage
            </Tag>
          )}
          
          {appliedFilters.margin !== 10 && (
            <Tag
              mr={2}
              mb={2}
              colorScheme="green"
              variant="subtle"
              borderRadius="full"
              size="sm"
            >
              <Box as="span" mr={1}>💰</Box>
              {formatCurrency(appliedFilters.margin)} Margin
            </Tag>
          )}
          
          {appliedFilters.airbag_threshold !== 50 && (
            <Tag
              mr={2}
              mb={2}
              colorScheme="green"
              variant="subtle"
              borderRadius="full"
              size="sm"
            >
              <Box as="span" mr={1}>🛟</Box>
              Airbag: {isAirbagOff(appliedFilters.airbag_threshold) ? "Off" : `${appliedFilters.airbag_threshold}%`}
            </Tag>
          )}
          
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              setFilters(initialFilters);
              setAppliedFilters(initialFilters);
            }}
            colorScheme="red"
            ml={2}
          >
            Clear All
          </Button>
        </Box>
      )}
      
      {/* Results section */}
      <ModelsOverview
        data={performanceData}
        isLoading={performanceQuery.isLoading || isManualLoading}
        filters={appliedFilters}
      />
    </Box>
  );
};

// Component to display models overview
// Skeleton loading components
const SkeletonModelCard = () => {
  const cardBg = "rgba(15, 27, 14, 0.8)";
  const borderColor = "rgba(78, 224, 53, 0.2)";
  
  return (
    <Card
      p={4}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
      borderRadius="xl"
      overflow="hidden"
      backdropFilter="blur(10px)"
    >
      <Flex justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Flex alignItems="center">
          <Box
            w="48px"
            h="48px"
            borderRadius="full"
            bg="rgba(255, 255, 255, 0.1)"
            mr={3}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="0"
              left="-100%"
              width="100%"
              height="100%"
              background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
              animation="skeleton 1.5s ease-in-out infinite"
            />
          </Box>
          <Box>
            <Box
              h="20px"
              w="120px"
              bg="rgba(255, 255, 255, 0.1)"
              borderRadius="md"
              mb={2}
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top="0"
                left="-100%"
                width="100%"
                height="100%"
                background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
                animation="skeleton 1.5s ease-in-out infinite"
              />
            </Box>
            <Box
              h="16px"
              w="80px"
              bg="rgba(255, 255, 255, 0.1)"
              borderRadius="md"
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top="0"
                left="-100%"
                width="100%"
                height="100%"
                background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
                animation="skeleton 1.5s ease-in-out infinite"
              />
            </Box>
          </Box>
        </Flex>
      </Flex>
      
      <SimpleGrid columns={2} spacing={3} mb={3}>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md">
            <Box
              h="12px"
              w="60px"
              bg="rgba(255, 255, 255, 0.1)"
              borderRadius="md"
              mb={2}
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top="0"
                left="-100%"
                width="100%"
                height="100%"
                background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
                animation="skeleton 1.5s ease-in-out infinite"
              />
            </Box>
            <Box
              h="16px"
              w="40px"
              bg="rgba(255, 255, 255, 0.1)"
              borderRadius="md"
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top="0"
                left="-100%"
                width="100%"
                height="100%"
                background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
                animation="skeleton 1.5s ease-in-out infinite"
              />
            </Box>
          </Box>
        ))}
      </SimpleGrid>
      
      <Box bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md">
        <Box
          h="12px"
          w="60px"
          bg="rgba(255, 255, 255, 0.1)"
          borderRadius="md"
          mb={2}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="-100%"
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
            animation="skeleton 1.5s ease-in-out infinite"
          />
        </Box>
        <Box
          h="16px"
          w="80px"
          bg="rgba(255, 255, 255, 0.1)"
          borderRadius="md"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="-100%"
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
            animation="skeleton 1.5s ease-in-out infinite"
          />
        </Box>
      </Box>
    </Card>
  );
};

const SkeletonTableRow = () => (
  <Tr>
    <Td borderColor="rgba(255, 255, 255, 0.1)">
      <HStack spacing={3}>
        <Box
          w="24px"
          h="24px"
          borderRadius="full"
          bg="rgba(255, 255, 255, 0.1)"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="-100%"
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
            animation="skeleton 1.5s ease-in-out infinite"
          />
        </Box>
        <Box
          h="16px"
          w="120px"
          bg="rgba(255, 255, 255, 0.1)"
          borderRadius="md"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="-100%"
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
            animation="skeleton 1.5s ease-in-out infinite"
          />
        </Box>
      </HStack>
    </Td>
    {[1, 2, 3, 4, 5].map((i) => (
      <Td key={i} isNumeric borderColor="rgba(255, 255, 255, 0.1)">
        <Box
          h="16px"
          w="60px"
          bg="rgba(255, 255, 255, 0.1)"
          borderRadius="md"
          ml="auto"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="0"
            left="-100%"
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
            animation="skeleton 1.5s ease-in-out infinite"
          />
        </Box>
      </Td>
    ))}
  </Tr>
);

const ModelsOverview = ({ data, isLoading, filters }) => {
  const cardBg = "rgba(15, 27, 14, 0.8)";
  const borderColor = "rgba(78, 224, 53, 0.2)";
  const accentColor = "green.400";
  
  // UI States
  const [sortField, setSortField] = useState('net_pct');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  
  
  if (isLoading) {
    return (
      <Box>
        {/* Skeleton CSS */}
        <style>
          {`
            @keyframes skeleton {
              0% {
                left: -100%;
              }
              100% {
                left: 100%;
              }
            }
          `}
        </style>
        
        {/* View Mode Toggle */}
        <Flex justify="space-between" align="center" mb={4}>
          <HStack>
            <Text fontSize="sm" color="gray.400">View:</Text>
            <ButtonGroup size="sm" isAttached variant="outline" colorScheme="green">
              <Button
                leftIcon={<FiList />}
                onClick={() => setViewMode('table')}
                colorScheme={viewMode === 'table' ? 'green' : undefined}
                borderRadius="md"
              >
                Table
              </Button>
              <Button
                leftIcon={<FiBarChart2 />}
                onClick={() => setViewMode('grid')}
                colorScheme={viewMode === 'grid' ? 'green' : undefined}
                borderRadius="md"
              >
                Grid
              </Button>
            </ButtonGroup>
          </HStack>
        </Flex>
        
        {/* Skeleton Grid View */}
        {viewMode === 'grid' && (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonModelCard key={i} />
            ))}
          </SimpleGrid>
        )}
        
        {/* Skeleton Table View */}
        {viewMode === 'table' && (
          <Card
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
            overflow="hidden"
            borderRadius="xl"
            backdropFilter="blur(10px)"
          >
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th color="gray.300">Model</Th>
                    <Th isNumeric color="gray.300">Trades</Th>
                    <Th isNumeric color="gray.300">Win Rate</Th>
                    <Th isNumeric color="gray.300">Net %</Th>
                    <Th isNumeric color="gray.300">Profit</Th>
                    <Th isNumeric color="gray.300">Balance</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <SkeletonTableRow key={i} />
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Card>
        )}
      </Box>
    );
  }
  
  if (!data || !data.models || Object.keys(data.models).length === 0) {
    return (
      <Box 
        py={12} 
        textAlign="center" 
        bg="rgba(0, 0, 0, 0.3)" 
        borderRadius="xl" 
        borderWidth="1px" 
        borderColor="whiteAlpha.100"
      >
        <Icon as={FiSearch} boxSize={10} color="gray.400" mb={4} />
        <Heading size="md" color="gray.400" mb={2}>No models found</Heading>
        <Text color="gray.500" mb={4}>Try adjusting your filter parameters</Text>
        <Text fontSize="sm" color="gray.400">
          Current filters: {Object.entries(filters).filter(([key, value]) => 
            key !== 'start_time' && key !== 'end_time' && 
            (key === 'session_filter' && value !== 'All' || 
             key === 'direction_filter' && value !== 'All' ||
             key === 'margin' && value !== 10 ||
             key === 'leverage' && value !== 20 ||
             key === 'airbag_threshold' && value !== 50)
          ).map(([key, value]) => `${key}: ${value}`).join(', ') || 'Default'}
        </Text>
      </Box>
    );
  }
  
  const models = data?.models || [];

  // Convert models object to array for easier sorting
  const modelsArray = Object.entries(models).map(([modelId, model]) => ({
    id: modelId,
    ...model
  }));
  
  // Sort the models array
  const sortedModels = [...modelsArray].sort((a, b) => {
    if (sortDirection === 'asc') {
      return a[sortField] - b[sortField];
    } else {
      return b[sortField] - a[sortField];
    }
  });
  
  // Find the best model
  const bestModel = sortedModels.length > 0 ? sortedModels[0] : null;
  
  // Handle sort change
  const handleSortChange = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Function to generate model avatar URL based on model name
  const getModelAvatarUrl = (model) => {
    const modelName = model.display || `AI Model ${model.id}`;
    // Using DiceBear API with "bottts" style for AI/robot-themed avatars with background - Updated color to green
    return `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(modelName)}&backgroundColor=dcfce7,bbf7d0,86efac,4ade80,22c55e&scale=85`;
  };
  
  // Function to generate model avatar color based on performance (for fallback)
  const getModelAvatarColor = (model) => {
    if (model.net_pct >= 10) return "green.500";
    if (model.net_pct >= 5) return "green.400";
    if (model.net_pct > 0) return "green.300";
    if (model.net_pct > -5) return "orange.400";
    return "red.400";
  };
  
  // Function to generate model avatar content (fallback)
  const getModelAvatarContent = (model) => {
    return model.display ? model.display.charAt(0).toUpperCase() : "M";
  };
  
  return (
    <Box>
  
      
      {/* View Mode Toggle */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          <Text fontSize="sm" color="gray.400">View:</Text>
          <ButtonGroup size="sm" isAttached variant="outline" colorScheme="green">
            <Button
              leftIcon={<FiList />}
              onClick={() => setViewMode('table')}
              colorScheme={viewMode === 'table' ? 'green' : undefined}
              borderRadius="md"
            >
              Table
            </Button>
            <Button
              leftIcon={<FiBarChart2 />}
              onClick={() => setViewMode('grid')}
              colorScheme={viewMode === 'grid' ? 'green' : undefined}
              borderRadius="md"
            >
              Grid
            </Button>
          </ButtonGroup>
        </HStack>
      </Flex>
      
      {/* Grid View */}
      {viewMode === 'grid' && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {sortedModels.map((model) => {
            const isProfitable = model.net_pct > 0;
            const isTheBest = model.id === bestModel?.id;
            
            return (
              <Card
                key={model.id}
                p={4}
                bg={cardBg}
                borderWidth={isTheBest ? "2px" : "1px"}
                borderColor={isTheBest ? "green.400" : borderColor}
                boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-3px)",
                  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
                  borderColor: isProfitable ? "green.400" : "red.400"
                }}
                borderRadius="xl"
                overflow="hidden"
                position="relative"
                backdropFilter="blur(10px)"
              >
                {isTheBest && (
                  <Box
                    position="absolute"
                    top="-1px"
                    right="-1px"
                    bg="green.500"
                    color="white"
                    px={2}
                    py={1}
                    fontSize="xs"
                    fontWeight="bold"
                    borderBottomLeftRadius="md"
                  >
                    BEST
                  </Box>
                )}
                
                <Flex justifyContent="space-between" alignItems="flex-start" mb={3}>
                  <Flex alignItems="center">
                    <Avatar
                      src={getModelAvatarUrl(model)}
                      bg={getModelAvatarColor(model)}
                      color="white"
                      name={getModelAvatarContent(model)}
                      size="md"
                      mr={3}
                      borderWidth="2px"
                      borderColor={getModelAvatarColor(model)}
                    />
                    <Box>
                      <Text fontWeight="medium" color="white" fontSize="lg">
                        {model.display || `AI Model ${model.id}`}
                      </Text>
                      <Box>
                        {isProfitable ? (
                          <Badge colorScheme="green">Profitable</Badge>
                        ) : (
                          <Badge colorScheme="red">Loss</Badge>
                        )}
                      </Box>
                    </Box>
                  </Flex>
                </Flex>
                
                <SimpleGrid columns={2} spacing={3} mb={3}>
                  <Box bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md">
                    <Text fontSize="xs" color="gray.400">Trades:</Text>
                    <Text color="white" fontWeight="medium" fontSize="lg">{model.total_trades}</Text>
                  </Box>
                  <Box bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md">
                    <Text fontSize="xs" color="gray.400">Winrate:</Text>
                    <Text color="white" fontWeight="medium" fontSize="lg">{model.winrate?.toFixed(1)}%</Text>
                  </Box>
                  <Box bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md">
                    <Text fontSize="xs" color="gray.400">Max Concurrent:</Text>
                    <Text color="white" fontWeight="medium" fontSize="lg">{model.max_open || 0}</Text>
                  </Box>
                  <Box bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md">
                    <Text fontSize="xs" color="gray.400">Net %:</Text>
                    <Text
                      fontWeight="medium"
                      fontSize="lg"
                      color={getValueColor(model.net_pct)}
                    >
                      {formatPercent(model.net_pct)}
                    </Text>
                  </Box>
                </SimpleGrid>
                
                <Box bg="rgba(0, 0, 0, 0.3)" p={2} borderRadius="md" mb={3}>
                  <Text fontSize="xs" color="gray.400">Profit:</Text>
                  <Text
                    fontWeight="medium"
                    fontSize="lg"
                    color={getValueColor(model.sim)}
                  >
                    {formatCurrency(model.sim)}
                  </Text>
                </Box>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
      
      {/* Table View */}
      {viewMode === 'table' && (
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.2)"
          overflow="hidden"
          borderRadius="xl"
          backdropFilter="blur(10px)"
        >
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th cursor="pointer" onClick={() => handleSortChange('id')} color="gray.300">
                    <Flex align="center">
                      <Text>Model</Text>
                      {sortField === 'id' && (
                        <Icon 
                          as={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                          ml={1} 
                          color={accentColor}
                        />
                      )}
                    </Flex>
                  </Th>
                  <Th 
                    isNumeric 
                    cursor="pointer" 
                    onClick={() => handleSortChange('total_trades')}
                    color="gray.300"
                  >
                    <Flex align="center" justify="flex-end">
                      <Text>Trades</Text>
                      {sortField === 'total_trades' && (
                        <Icon 
                          as={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                          ml={1} 
                          color={accentColor}
                        />
                      )}
                    </Flex>
                  </Th>
                  <Th 
                    isNumeric 
                    cursor="pointer" 
                    onClick={() => handleSortChange('winrate')}
                    color="gray.300"
                  >
                    <Flex align="center" justify="flex-end">
                      <Text>Win Rate</Text>
                      {sortField === 'winrate' && (
                        <Icon 
                          as={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                          ml={1} 
                          color={accentColor}
                        />
                      )}
                    </Flex>
                  </Th>
                  <Th 
                    isNumeric 
                    cursor="pointer" 
                    onClick={() => handleSortChange('net_pct')}
                    color="gray.300"
                  >
                    <Flex align="center" justify="flex-end">
                      <Text>Net %</Text>
                      {sortField === 'net_pct' && (
                        <Icon 
                          as={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                          ml={1} 
                          color={accentColor}
                        />
                      )}
                    </Flex>
                  </Th>
                  <Th 
                    isNumeric 
                    cursor="pointer" 
                    onClick={() => handleSortChange('sim')}
                    color="gray.300"
                  >
                    <Flex align="center" justify="flex-end">
                      <Text>Profit</Text>
                      {sortField === 'sim' && (
                        <Icon 
                          as={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                          ml={1} 
                          color={accentColor}
                        />
                      )}
                    </Flex>
                  </Th>
                  <Th isNumeric color="gray.300">Balance</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedModels.map((model) => (
                  <Tr
                    key={model.id}
                    _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                    transition="background-color 0.2s"
                    bg={model.id === bestModel?.id ? "rgba(16, 185, 129, 0.1)" : undefined}
                  >
                    <Td fontWeight="medium" color="white" borderColor="rgba(255, 255, 255, 0.1)">
                      <HStack spacing={3}>
                        <Avatar
                          src={getModelAvatarUrl(model)}
                          size="xs"
                          name={getModelAvatarContent(model)}
                          bg={getModelAvatarColor(model)}
                          color="white"
                          borderWidth="1px"
                          borderColor={getModelAvatarColor(model)}
                        />
                        <VStack align="start" spacing={0}>
                          <HStack>
                            {model.id === bestModel?.id && (
                              <Badge colorScheme="green" size="sm">BEST</Badge>
                            )}
                            <Text fontSize="sm" fontWeight="medium" color="white">
                              {model.display || `AI Model ${model.id}`}
                            </Text>
                          </HStack>
                        </VStack>
                      </HStack>
                    </Td>
                    <Td isNumeric color="white" borderColor="rgba(255, 255, 255, 0.1)">{model.total_trades}</Td>
                    <Td isNumeric color="white" borderColor="rgba(255, 255, 255, 0.1)">{model.winrate?.toFixed(1)}%</Td>
                    <Td isNumeric color={getValueColor(model.net_pct)} borderColor="rgba(255, 255, 255, 0.1)">
                      {formatPercent(model.net_pct)}
                    </Td>
                    <Td isNumeric color={getValueColor(model.sim)} borderColor="rgba(255, 255, 255, 0.1)">
                      {formatCurrency(model.sim)}
                    </Td>
                    <Td isNumeric fontWeight="bold" color="white" borderColor="rgba(255, 255, 255, 0.1)">
                      {formatCurrency(model.balance)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default Performance;